/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { useToast } from "../../contexts/ToastContext";
import { cn } from "../../lib/utils";

/**
 * A labeled identifier the coordinator can copy with one tap — the bridge from a stream to the
 * record in Healthie. Click copies the value and toasts; the icon flips to a check briefly.
 */
export function CopyableId({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      showToast("success", `${label} ID copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showToast("error", `Couldn't copy the ${label} ID`);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={`Copy ${label} ID`}
      className={cn(
        "group inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs transition-colors hover:border-border-hover",
        className,
      )}
    >
      <span className="shrink-0 text-text-tertiary">{label}</span>
      <span className="min-w-0 truncate font-mono text-text-secondary group-hover:text-text-primary">
        {value}
      </span>
      {copied ? (
        <Check size={12} className="shrink-0 text-success" />
      ) : (
        <Copy size={12} className="shrink-0 text-text-tertiary group-hover:text-text-primary" />
      )}
    </button>
  );
}
